import { SxProps, styled, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import React from 'react';
import { Link as RouterLink } from 'react-router';
import { Color } from 'src/constants/palette';
import { parseFileUrl } from 'src/helpers/fileUrl';
import { useTrack } from '../analytics';
import { AnalyticsEvent, AnalyticsEventType, ClickElementType } from '../analytics/events';

const StyledExternalLink = styled('a')(({ theme }) => ({
    color: theme.resqPalette.text.link,
    textDecoration: 'underline',
    fontWeight: 400,
    ':hover': {
        color: theme.resqPalette.text.primary,
        filter: 'none',
        textDecoration: 'underline',
    },
}));

const StyledRouterLink = styled(RouterLink)(({ theme }) => ({
    color: theme.resqPalette.text.link,
    fontWeight: 500,
}));
export interface BaseLinkProps extends Pick<
    React.HTMLProps<HTMLAnchorElement>,
    'target' | 'rel' | 'style' | 'download'
> {
    trackingId?: string;
    children: React.ReactNode;
}

type TriggerLinkProps = {
    onClick: () => void;
    to?: undefined;
    sx?: undefined;
};

type RouterLinkProps = {
    to?: string | null;
    onClick?: undefined;
    style?: undefined;
    sx?: SxProps<Theme> | undefined;
};

type LinkProps = BaseLinkProps & (TriggerLinkProps | RouterLinkProps);

/** Please set trackingId explicitly if link is for a dynamic label
 * (eg. phone numbers, emails, work order codes, etc.)
 */
export const Link = ({ to, onClick, trackingId, children, sx, ...props }: LinkProps) => {
    const track = useTrack();
    const defaultRouterLinkProps: SxProps<Theme> = {
        textDecoration: 'underline',
        color: (theme) => theme.resqPalette.text.link,
        '&:hover': {
            color: (theme) => theme.resqPalette.text.primary,
            filter: 'none',
            textDecoration: 'underline',
        },
    };

    if (!to) {
        return (
            <StyledExternalLink
                {...props}
                sx={sx}
                onClick={() => {
                    track(AnalyticsEvent.ElementClicked, {
                        trackingId,
                        label: children?.toString() || null,
                        link: to || null,
                        elementType: ClickElementType.LINK,
                        eventType: AnalyticsEventType.ANALYTICS,
                        option: null,
                    });
                    onClick?.();
                }}
                onKeyDown={() => {
                    track(AnalyticsEvent.ElementClicked, {
                        trackingId,
                        label: children?.toString() || null,
                        link: to || null,
                        elementType: ClickElementType.LINK,
                        eventType: AnalyticsEventType.ANALYTICS,
                        option: null,
                    });
                    onClick?.();
                }}
            >
                {children}
            </StyledExternalLink>
        );
    }

    const fileProps = parseFileUrl(to);
    if (to.startsWith('/')) {
        return (
            <StyledRouterLink
                {...props}
                sx={sx ?? defaultRouterLinkProps}
                to={to}
                onClick={() => {
                    track(AnalyticsEvent.ElementClicked, {
                        trackingId,
                        label: children?.toString() || null,
                        link: to,
                        elementType: ClickElementType.LINK,
                        eventType: AnalyticsEventType.ANALYTICS,
                        option: null,
                    });
                    if (fileProps) {
                        track(AnalyticsEvent.FileDownloaded, {
                            trackingId,
                            label: children?.toString() || null,
                            option: null,
                            link: null,
                            elementType: null,
                            fileLocation: fileProps.fileLocation,
                            fileName: fileProps.fileName,
                            fileType: fileProps.fileType,
                            eventType: AnalyticsEventType.ANALYTICS,
                        });
                    }
                }}
                onKeyDown={() => {
                    track(AnalyticsEvent.ElementClicked, {
                        trackingId,
                        label: children?.toString() || null,
                        link: to,
                        elementType: ClickElementType.LINK,
                        eventType: AnalyticsEventType.ANALYTICS,
                        option: null,
                    });
                    if (fileProps) {
                        track(AnalyticsEvent.FileDownloaded, {
                            trackingId,
                            label: children?.toString() || null,
                            option: null,
                            link: null,
                            elementType: null,
                            fileLocation: fileProps.fileLocation,
                            fileName: fileProps.fileName,
                            fileType: fileProps.fileType,
                            eventType: AnalyticsEventType.ANALYTICS,
                        });
                    }
                }}
            >
                {children}
            </StyledRouterLink>
        );
    }
    return (
        <StyledExternalLink
            {...props}
            sx={sx}
            href={to}
            onClick={() => {
                track(AnalyticsEvent.ElementClicked, {
                    trackingId,
                    label: children?.toString() || null,
                    link: to,
                    elementType: ClickElementType.LINK,
                    eventType: AnalyticsEventType.ANALYTICS,
                    option: null,
                });
                if (fileProps) {
                    track(AnalyticsEvent.FileDownloaded, {
                        trackingId,
                        label: children?.toString() || null,
                        fileLocation: fileProps.fileLocation,
                        fileName: fileProps.fileName,
                        fileType: fileProps.fileType,
                        eventType: AnalyticsEventType.ANALYTICS,
                        option: null,
                        link: null,
                        elementType: null,
                    });
                }
            }}
            onKeyDown={() => {
                track(AnalyticsEvent.ElementClicked, {
                    trackingId,
                    label: children?.toString() || null,
                    link: to,
                    elementType: ClickElementType.LINK,
                    eventType: AnalyticsEventType.ANALYTICS,
                    option: null,
                });
                if (fileProps) {
                    track(AnalyticsEvent.FileDownloaded, {
                        trackingId,
                        label: children?.toString() || null,
                        fileLocation: fileProps.fileLocation,
                        fileName: fileProps.fileName,
                        fileType: fileProps.fileType,
                        eventType: AnalyticsEventType.ANALYTICS,
                        option: null,
                        link: null,
                        elementType: null,
                    });
                }
            }}
        >
            {children}
        </StyledExternalLink>
    );
};

export interface TextWithEmbeddedLinkProps {
    leadingText: string;
    linkText: string;
    followingText: string;
    link: string;
    linkColor?: keyof Theme['resqPalette']['text'];
}

export const TextWithEmbeddedLink = ({
    leadingText,
    linkText,
    followingText,
    link,
    linkColor,
}: TextWithEmbeddedLinkProps) => {
    const theme = useTheme();
    const isExternalLink = link.includes('://');
    return (
        <div>
            {leadingText}
            {isExternalLink ? (
                <Link
                    sx={{
                        color: linkColor
                            ? ((theme.resqPalette.text as any)[linkColor] as Color)
                            : undefined,
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    to={link}
                >
                    {linkText}
                </Link>
            ) : (
                <Link
                    sx={{
                        color: linkColor
                            ? ((theme.resqPalette.text as any)[linkColor] as Color)
                            : undefined,
                    }}
                    to={link}
                >
                    {linkText}
                </Link>
            )}
            {followingText}
        </div>
    );
};

export interface EmbeddedLinkProps {
    leadingText?: string;
    followingText?: string;
    link?: string;
    onClick?: () => void;
    children?: React.ReactNode;
}

export const EmbeddedLink = ({
    leadingText,
    children,
    followingText,
    link,
    onClick,
    ...props
}: EmbeddedLinkProps) => {
    return (
        <>
            {leadingText}
            {leadingText ? ' ' : ''}
            <StyledExternalLink href={link} onClick={onClick} {...props}>
                {children}
            </StyledExternalLink>
            {followingText ? ' ' : ''}
            {followingText}
        </>
    );
};
